import axios from "axios";
import {
  MaintenanceStatus,
  AdminHealthResponse,
  AdminInfoResponse,
  MaintenanceToggleRequest,
  MaintenanceResponse,
  ResendDeathTileResponse,
  UserReferralRewardsResponse,
  UserData,
  ProbLossConfig,
  ReferredUsersResponse,
  BatchProbLossUpdateResponse,
} from "../types";
import { adminAuthService } from "./adminAuth";
import { API_BASE_URL, ApiError } from "./api";

class AdminApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = `${API_BASE_URL}/admin`;
  }

  /**
   * Get authorization headers for admin requests
   */
  private getAuthHeaders(): Record<string, string> {
    const token = adminAuthService.getAdminToken();
    if (!token) {
      throw new ApiError("Admin authentication required", 401, "ADMIN_AUTH_REQUIRED");
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Handle admin API errors
   */
  private handleAdminError(error: any): never {
    if (error.response?.status === 403) {
      // Admin privileges required - revoke admin status
      adminAuthService.revokeAdminStatus();
      throw new ApiError(
        "Admin privileges required",
        403,
        "ADMIN_PRIVILEGES_REQUIRED"
      );
    }

    if (error.response?.data) {
      const { error: message, code, timestamp } = error.response.data;
      const statusCode = error.response.status;

      throw new ApiError(
        message || "Admin API error",
        statusCode,
        code || "ADMIN_API_ERROR",
        timestamp
      );
    }

    if (error.request) {
      throw new ApiError(
        "Network error - please check your connection",
        0,
        "NETWORK_ERROR"
      );
    }

    throw new ApiError(
      error.message || "Unknown admin API error",
      500,
      "UNKNOWN_ADMIN_ERROR"
    );
  }

  /**
   * Confirm admin access after successful API call
   */
  private confirmAdminAccess(): void {
    adminAuthService.confirmAdminStatus();
  }

  /**
   * Get maintenance mode status
   */
  async getMaintenanceStatus(): Promise<MaintenanceStatus> {
    try {
      const response = await axios.get<MaintenanceResponse>(
        `${this.baseURL}/maintenance/status`,
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Enable maintenance mode
   */
  async enableMaintenance(message?: string): Promise<MaintenanceStatus> {
    try {
      const requestData: MaintenanceToggleRequest = message ? { message } : {};
      
      const response = await axios.post<MaintenanceResponse>(
        `${this.baseURL}/maintenance/enable`,
        requestData,
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Disable maintenance mode
   */
  async disableMaintenance(): Promise<MaintenanceStatus> {
    try {
      const response = await axios.post<MaintenanceResponse>(
        `${this.baseURL}/maintenance/disable`,
        {},
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Get admin health information
   */
  async getHealth(): Promise<AdminHealthResponse> {
    try {
      const response = await axios.get<AdminHealthResponse>(
        `${this.baseURL}/health`,
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Get admin system information
   */
  async getSystemInfo(): Promise<AdminInfoResponse> {
    try {
      const response = await axios.get<AdminInfoResponse>(
        `${this.baseURL}/info`,
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Resend death tile message for a game
   */
  async resendDeathTile(gameId: string): Promise<ResendDeathTileResponse> {
    try {
      if (!gameId || !gameId.trim()) {
        throw new ApiError("Game ID is required", 400, "MISSING_GAME_ID");
      }

      const response = await axios.post<ResendDeathTileResponse>(
        `${this.baseURL}/resend-death-tile/${encodeURIComponent(gameId)}`,
        {},
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Manually trigger streak reset for stale user streaks
   */
  async triggerStreakReset(): Promise<{ message: string; timestamp: string }> {
    try {
      const response = await axios.post(
        `${this.baseURL}/streak/reset`,
        {},
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return {
        message: response.data.message,
        timestamp: response.data.data.timestamp
      };
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Manually trigger draw completion for a specific date
   */
  async triggerDrawCompletion(date?: string): Promise<{ message: string; date: string }> {
    try {
      const requestData = date ? { date } : {};
      
      const response = await axios.post(
        `${this.baseURL}/draw/complete`,
        requestData,
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return {
        message: response.data.message,
        date: response.data.date
      };
    } catch (error) {
      this.handleAdminError(error);
    }
  }


  // ===============================
  // Weekly Reward Distribution Methods
  // ===============================

  /**
   * Get all pending reward distributions
   */
  async getPendingRewardDistributions(): Promise<{ distributions: WeeklyRewardDistribution[] }> {
    try {
      const response = await axios.get(
        `${this.baseURL}/rewards-distribution/pending`,
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Get reward distribution for a specific week
   */
  async getWeeklyRewardDistribution(weekStartDate: string): Promise<WeeklyRewardDistribution | null> {
    try {
      const response = await axios.get(
        `${this.baseURL}/rewards-distribution/${encodeURIComponent(weekStartDate)}`,
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      if (error && typeof error === 'object' && 'response' in error && 
          (error as any).response?.status === 404) {
        return null;
      }
      this.handleAdminError(error);
    }
  }

  /**
   * Calculate rewards for a specific week
   */
  async calculateWeeklyRewards(weekStartDate: string): Promise<{ distribution: WeeklyRewardDistribution; message: string }> {
    try {
      const response = await axios.post(
        `${this.baseURL}/rewards-distribution/calculate/${encodeURIComponent(weekStartDate)}`,
        {},
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Execute all rewards for a specific week
   */
  async executeAllWeeklyRewards(weekStartDate: string): Promise<ExecutionResult> {
    try {
      const response = await axios.post(
        `${this.baseURL}/rewards-distribution/execute-all/${encodeURIComponent(weekStartDate)}`,
        {},
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Execute specific reward types for a week
   */
  async executeSpecificWeeklyRewards(
    weekStartDate: string, 
    rewardTypes: WeeklyRewardType[]
  ): Promise<ExecutionResult> {
    try {
      const response = await axios.post(
        `${this.baseURL}/rewards-distribution/execute/${encodeURIComponent(weekStartDate)}`,
        { rewardTypes },
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Generate leaderboard snapshot for a specific week
   */
  async generateLeaderboardSnapshot(weekStartDate?: string): Promise<{ message: string; weekStartDate: string }> {
    try {
      const requestData = weekStartDate ? { weekStartDate } : {};
      
      const response = await axios.post(
        `${this.baseURL}/leaderboard/generate-snapshot`,
        requestData,
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return {
        message: response.data.message,
        weekStartDate: response.data.weekStartDate || response.data.data?.weekStartDate || weekStartDate || 'current week'
      };
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  // ===============================
  // Welcome Airdrop Management Methods
  // ===============================

  /**
   * Get welcome airdrop submissions with optional status filter
   */
  async getWelcomeAirdropSubmissions(status?: 'pending' | 'processed'): Promise<{ data: WelcomeAirdropSubmission[]; count: number; success: boolean }> {
    try {
      const params = status ? { status } : {};
      
      const response = await axios.get(
        `${this.baseURL}/airdrop/welcome/submissions`,
        { 
          headers: this.getAuthHeaders(),
          params
        }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Create a new welcome airdrop submission
   */
  async createWelcomeAirdropSubmission(submission: {
    walletAddress: string;
    xAccount: string;
    telegramUsername: string;
    dxfAmount: string;
  }): Promise<WelcomeAirdropSubmission> {
    try {
      const response = await axios.post(
        `${this.baseURL}/airdrop/welcome/submit`,
        submission,
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data.submission;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Process pending welcome airdrop submissions
   */
  async processPendingWelcomeAirdropSubmissions(): Promise<{
    processed: number;
    txHash: string;
    message: string;
    recipients: Array<{
      walletAddress: string;
      xAccount: string;
      telegramUsername: string;
      dxfAmount: string;
    }>;
  }> {
    try {
      const response = await axios.post(
        `${this.baseURL}/airdrop/welcome/proceed`,
        {},
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Get user referral rewards data
   */
  async getUserReferralRewards(walletAddress: string): Promise<UserReferralRewardsResponse> {
    try {
      if (!walletAddress || !walletAddress.trim()) {
        throw new ApiError("Wallet address is required", 400, "MISSING_WALLET_ADDRESS");
      }

      // Basic Ethereum address validation
      if (!/^0x[a-fA-F0-9]{40}$/.test(walletAddress)) {
        throw new ApiError(
          "Invalid wallet address format. Must be a valid Ethereum address (0x + 40 hex characters)",
          400,
          "INVALID_ADDRESS"
        );
      }

      const response = await axios.get<UserReferralRewardsResponse>(
        `${this.baseURL}/referral/rewards/${encodeURIComponent(walletAddress)}`,
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Get comprehensive user data
   */
  async getUser(walletAddress: string): Promise<UserData> {
    try {
      if (!walletAddress || !walletAddress.trim()) {
        throw new ApiError("Wallet address is required", 400, "MISSING_WALLET_ADDRESS");
      }

      // Basic Ethereum address validation
      if (!/^0x[a-fA-F0-9]{40}$/.test(walletAddress)) {
        throw new ApiError(
          "Invalid wallet address format. Must be a valid Ethereum address (0x + 40 hex characters)",
          400,
          "INVALID_ADDRESS"
        );
      }

      const response = await axios.get<UserData>(
        `${this.baseURL}/user/${encodeURIComponent(walletAddress)}`,
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Update user mark field
   */
  async updateUserMark(walletAddress: string, mark: string | null): Promise<UserData> {
    try {
      if (!walletAddress || !walletAddress.trim()) {
        throw new ApiError("Wallet address is required", 400, "MISSING_WALLET_ADDRESS");
      }

      // Basic Ethereum address validation
      if (!/^0x[a-fA-F0-9]{40}$/.test(walletAddress)) {
        throw new ApiError(
          "Invalid wallet address format. Must be a valid Ethereum address (0x + 40 hex characters)",
          400,
          "INVALID_ADDRESS"
        );
      }

      const response = await axios.put<UserData>(
        `${this.baseURL}/user/${encodeURIComponent(walletAddress)}/mark`,
        { mark },
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Update user's claim blocked status
   */
  async updateUserClaimBlocked(walletAddress: string, claimBlocked: boolean): Promise<UserData> {
    try {
      if (!walletAddress || !walletAddress.trim()) {
        throw new ApiError("Wallet address is required", 400, "MISSING_WALLET_ADDRESS");
      }

      // Basic Ethereum address validation
      if (!/^0x[a-fA-F0-9]{40}$/.test(walletAddress)) {
        throw new ApiError(
          "Invalid wallet address format. Must be a valid Ethereum address (0x + 40 hex characters)",
          400,
          "INVALID_ADDRESS"
        );
      }

      const response = await axios.put<UserData>(
        `${this.baseURL}/user/${encodeURIComponent(walletAddress)}/claim-blocked`,
        { claimBlocked },
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Update user's probLoss configuration
   * @param walletAddress - User's wallet address
   * @param probLossConfig - ProbLoss configuration object
   * @returns Updated user data
   */
  async updateUserProbLossConfig(
    walletAddress: string,
    probLossConfig: ProbLossConfig
  ): Promise<UserData> {
    try {
      if (!walletAddress || !walletAddress.trim()) {
        throw new ApiError("Wallet address is required", 400, "MISSING_WALLET_ADDRESS");
      }

      // Basic Ethereum address validation
      if (!/^0x[a-fA-F0-9]{40}$/.test(walletAddress)) {
        throw new ApiError(
          "Invalid wallet address format. Must be a valid Ethereum address (0x + 40 hex characters)",
          400,
          "INVALID_ADDRESS"
        );
      }

      const response = await axios.put<{ success: boolean; user: UserData }>(
        `${this.baseURL}/user/${encodeURIComponent(walletAddress)}/prob-loss-config`,
        probLossConfig,
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data.user;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new ApiError(
          error.response.data.error || 'Failed to update probLoss configuration',
          error.response.status,
          error.response.data.code
        );
      }
      this.handleAdminError(error);
    }
  }

  /**
   * Search users by wallet address or mark field
   */
  async searchUsers(query: string): Promise<UserData[]> {
    try {
      if (!query || !query.trim()) {
        throw new ApiError("Search query is required", 400, "MISSING_QUERY");
      }

      const response = await axios.get<UserData[]>(
        `${this.baseURL}/user/search`,
        {
          headers: this.getAuthHeaders(),
          params: { query: query.trim() }
        }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Get detailed information about all users referred by a specific wallet address
   * @param walletAddress - The referrer's wallet address
   * @returns Detailed referred users data
   */
  async getReferredUsers(walletAddress: string): Promise<ReferredUsersResponse> {
    try {
      if (!walletAddress || !walletAddress.trim()) {
        throw new ApiError("Wallet address is required", 400, "MISSING_WALLET_ADDRESS");
      }

      // Basic Ethereum address validation
      if (!/^0x[a-fA-F0-9]{40}$/.test(walletAddress)) {
        throw new ApiError(
          "Invalid wallet address format. Must be a valid Ethereum address (0x + 40 hex characters)",
          400,
          "INVALID_ADDRESS"
        );
      }

      const response = await axios.get<ReferredUsersResponse>(
        `${this.baseURL}/referral/referred-users/${encodeURIComponent(walletAddress)}`,
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      this.handleAdminError(error);
    }
  }

  /**
   * Batch update probLossConfig for multiple users
   * @param walletAddresses - Array of wallet addresses to update
   * @param probLossConfig - The ProbLossConfig to apply to all users
   * @returns Batch update result
   */
  async batchUpdateProbLossConfig(
    walletAddresses: string[],
    probLossConfig: ProbLossConfig
  ): Promise<BatchProbLossUpdateResponse> {
    try {
      if (!Array.isArray(walletAddresses) || walletAddresses.length === 0) {
        throw new ApiError(
          "walletAddresses must be a non-empty array",
          400,
          "INVALID_WALLET_ADDRESSES"
        );
      }

      // Validate each wallet address
      for (const addr of walletAddresses) {
        if (!/^0x[a-fA-F0-9]{40}$/.test(addr)) {
          throw new ApiError(
            `Invalid wallet address format: ${addr}`,
            400,
            "INVALID_WALLET_ADDRESS"
          );
        }
      }

      const response = await axios.put<BatchProbLossUpdateResponse>(
        `${this.baseURL}/users/batch-prob-loss-config`,
        { walletAddresses, probLossConfig },
        { headers: this.getAuthHeaders() }
      );

      this.confirmAdminAccess();
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new ApiError(
          error.response.data.error || error.response.data.message || 'Failed to batch update probLoss configuration',
          error.response.status,
          error.response.data.code
        );
      }
      this.handleAdminError(error);
    }
  }
}

// Types for reward distribution
interface WeeklyRewardDistribution {
  id?: string;
  weekStartDate: string;
  rewardsConfigId: string;
  gameCountBnbRewards: RewardEntry[];
  gameCountDxfRewards: RewardEntry[];
  dxfMiningBnbRewards: RewardEntry[];
  dxfMiningDxfRewards: RewardEntry[];
  txHashes: {
    gameCountBnb?: string;
    gameCountDxf?: string;
    dxfMiningBnb?: string;
    dxfMiningDxf?: string;
  };
  totalBnbAmount: string;
  totalDxfAmount: string;
  totalRecipients: number;
  calculatedAt: string;
  createdAt: string;
  updatedAt: string;
}

interface RewardEntry {
  walletAddress: string;
  rank: number;
  bnbAmount?: string;
  dxfAmount?: string;
}

type WeeklyRewardType = 'gameCountBnb' | 'gameCountDxf' | 'dxfMiningBnb' | 'dxfMiningDxf';

interface ExecutionResult {
  success: boolean;
  executedTypes: string[];
  txHashes: Record<string, string>;
  errors: string[];
}

interface WelcomeAirdropSubmission {
  id?: string;
  walletAddress: string;
  xAccount: string;
  telegramUsername: string;
  dxfAmount: string;
  status: 'pending' | 'processed';
  createdAt: string;
  processedAt?: string;
  txHash?: string;
}

export const adminApiService = new AdminApiService();
export type { WeeklyRewardDistribution, RewardEntry, WeeklyRewardType, ExecutionResult, WelcomeAirdropSubmission };